import type {ReactElement, TransitionEvent} from 'react';
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {isFacebookAccount, isInstagramAccount} from '@Components/social-media/account.vo';
import {SocialAccountLabel} from '@Components/social-media/components/social-account-label';
import type {PosterHashedId, SocialPostCover} from '@Components/social-media/publish-params.types';
import {MultiPostDesignActionPurposes, SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import {SocialPostCaption} from '@Components/social-media-wizard/components/social-post-caption';
import type {PublishableUrlUpdatePayload, SocialMediaResizeAjaxParams, SocialPostPlatformSelectionTransitionEventDetail} from '@Components/social-media/social-media.types';
import {
  MultiGraphicDesignSelectorModes,
  PosterResizeRequiredStatus,
  SocialPlatform,
  SocialPostCaptionOverwriteEvent,
  SocialPostPlatformSelectionTransitionEvent,
} from '@Components/social-media/social-media.types';
import {TitleInput} from '@Components/social-media-wizard/components/title-input';
import {
  useAccountIdsToPublishTo,
  useAccountResizeCompletedStatusForPosters,
  useAccountsToPublishTo,
  usePostPublishingParamsForAccount,
  usePublishingParams,
  usePublishingStatusForAccountWithMediaType,
  useReelPublishingParamsForAccount,
  useSocialMediaWizardPost,
  useSocialPostPublishingAction,
  useStoryGuidelineForAccount,
  useStoryPublishingParamsForAccount,
  useWizardStep,
} from '@Components/social-media/social-media.hooks';
import {ReelToggler} from '@Components/social-media-wizard/components/reel-toggler';
import {
  socialAccountPostCoverValidations,
  SocialMediaWizardContentSize,
  SocialMediaWizardMediaTypeRadioButtonIds,
  SocialMediaWizardStep,
  WizardMobileNavView,
} from '@Components/social-media-wizard/social-media-wizard.types';
import {FieldSpacer} from '@Components/social-media-wizard/components/field-spacer';
import {TiktokPublishingOptions} from '@Components/social-media-wizard/components/tiktok-publishing-options';
import {
  getPostId,
  useAccountNeedsResizeMapForDesigns,
  useDesignIdsToPublish,
  useDesignsGraphicsMetaForAccountWithMediaType,
  useDoesAnyPosterRequireResizeForAccount,
  useHasAnyPosterResizeErroredForAccount,
  useIsAnyPosterLoadingForAccount,
  useIsAnyPosterResizingForAccount,
  useIsAnyPosterResizingForAccountWithMediaType,
  useIsPublishingVideoForAccount,
  usePublishableURLsForAccount,
  useWizardDesignsGraphicsMeta,
  useWizardMobileNavView,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {LinkedInPrivacySelector} from '@Components/social-media-wizard/components/linkedin-privacy-selector';
import {Button, Size, Type} from '@Components/button';
import {
  removeAccountFromPublishingParams,
  removeAccountIdFromPublishing,
  setAccountResizeStatusForMediaType,
  setMediaTypeSelectionToPost,
  setMediaTypeSelectionToPostAndStory,
  setMediaTypeSelectionToReel,
  setMediaTypeSelectionToReelAndStory,
  setMediaTypeSelectionToStory,
  updateDesignIdToResize,
  updateGraphicURLInGraphicsMetaForDesign,
  updateMultiGraphicDesignSelectorMode,
  updatePublishableUrl,
  updatePublishingParams,
  updatePublishingParamSocialPostCover,
  updateWizardStep,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {PublishingPost} from '@Components/social-media-wizard/components/publishing-post';
import {YouTubePrivacySelector} from '@Components/social-media-wizard/components/youtube-privacy-selector';
import {
  doHideRemoveAccountConfirmationForUser,
  generateCoverForPost,
  getHideSMRemoveAccountConfirmationPreference,
  getResizeConfigForGATracking,
} from '@Libraries/social-media-wizard-library';
import {getSocialPlatformName, makeSocialMediaResizeAjaxCall} from '@Libraries/social-media';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {LoadingStates} from '@Utils/loading.util';
import type {AccountResizeStatusForDesignPayload} from '@Components/social-media-wizard/components/social-media-resize-options/social-media-resize-options.types';
import {AccountResizeStatus, ResizeOptionsConfig} from '@Components/social-media-wizard/components/social-media-resize-options/social-media-resize-options.types';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import {SocialPostContentType} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import {SocialPostContent} from '@Components/social-media/components/social-post-content-multiposting';
import type {SocialPostsDesignActionModalProps} from '@Components/modals/social-post-design-action-modal';
import {openDesignActionModal, PANEL_ID as SocialPostSelectDesignPanelID} from '@Components/modals/social-post-design-action-modal';
import {PinterestBoardId} from '@Components/social-media-wizard/components/pinterest-board-id';
import {PinterestWebsiteLink} from '@Components/social-media-wizard/components/pinterest-website-link';
import {GA4EventName, GA4EventParamName, trackPublishingGA4Events} from '@Libraries/ga-events';
import type {RadioButtonOption} from '@Components/radio-button/radio-button';
import {RadioButton} from '@Components/radio-button/radio-button';
import {SocialMediaPublishParamsStoryGuideline} from '@Components/social-media-wizard/components/social-media-publish-params-story-guideline';
import type {SocialMediaSelectCoverModalProps} from '@Components/modals/social-media-select-cover-modal';
import {openSelectCoverModal} from '@Components/modals/social-media-select-cover-modal';
import type {CropData, CropperPanelApplyResponse} from '@Panels/cropper-panel';
import type {SocialPostCoverParams} from '@Panels/social-media-select-cover-panel/social-media-select-cover-panel.types';
import {CoverSource} from '@Panels/social-media-select-cover-panel/social-media-select-cover-panel.types';
import {setIsLoading, setShowCoverGenerationError} from '@Panels/social-media-select-cover-panel/social-media-select-cover-panel-reducer';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {setPanelMessage, setShowLoading} from '@Panels/cropper-panel/cropper-panel-reducer';
import {PANEL_MESSAGE_TYPE} from '@Components/panel/panel.types';
import {Pill, PILL_BACKGROUND_COLOR, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import styles from './social-media-post-publish-params.module.scss';
import {useAppDispatch} from '@/hooks';
import {getCacheBustedURL} from '@Utils/url.util';
import {openConfirmDeleteModal} from '@Modals/confirm-delete-modal';

interface SocialMediaPostPublishParamsProps {
  account: SocialAccount;
  size?: SocialMediaWizardContentSize;
  platformMediaType?: PlatformMediaType;
}

export function SocialMediaPostPublishParams({
  account,
  size = SocialMediaWizardContentSize.XLARGE,
  platformMediaType = PlatformMediaType.POST,
}: SocialMediaPostPublishParamsProps): ReactElement<any> | null {
  const wizardMobileNavView = useWizardMobileNavView();
  const publishingAction = useSocialPostPublishingAction();
  const publishingParams = usePublishingParams();
  const postPublishingParamsForAccount = usePostPublishingParamsForAccount(account.id);
  const reelPublishingParamsForAccount = useReelPublishingParamsForAccount(account.id);
  const storyPublishingParamsForAccount = useStoryPublishingParamsForAccount(account.id);
  const accountNeedsResizingMapForDesigns = useAccountNeedsResizeMapForDesigns(account.id);
  const dispatch = useAppDispatch();
  const isVideoDesign = useIsPublishingVideoForAccount();
  const platformMediaTypeRef = useRef(platformMediaType);
  const {isSmallScreen, isExtraSmallScreen} = useContext(SocialMediaWizardContainerQueriesContext);
  const isWeb = size === SocialMediaWizardContentSize.XLARGE || size === SocialMediaWizardContentSize.LARGE || size === SocialMediaWizardContentSize.MEDIUM;
  const resizeButtonRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [titleErrorMessage, setTitleErrorMessage] = useState<string | null>(null);
  const loadingState: LoadingStates = usePublishingStatusForAccountWithMediaType(account.id, platformMediaTypeRef.current);
  const accountResizeStatus = useAccountResizeCompletedStatusForPosters(account, platformMediaTypeRef.current);
  const wizardPost = useSocialMediaWizardPost();
  const wizardStep = useWizardStep();
  const previewParentRef = useRef<HTMLDivElement>(null);
  const animatingElementRef = useRef<HTMLDivElement>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const designIdsToPublishTo = useDesignIdsToPublish();
  const accountsToPublishTo = useAccountsToPublishTo();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const designsGraphicsMeta = useWizardDesignsGraphicsMeta();
  const isAnyPosterResizingForForAnyMediaType = useIsAnyPosterResizingForAccount(account.id);
  const designsGraphicsMetaForAccount = useDesignsGraphicsMetaForAccountWithMediaType(account.id, platformMediaTypeRef.current);
  const isAnyPosterResizingForAccount = useIsAnyPosterResizingForAccountWithMediaType(account.id, platformMediaTypeRef.current);
  const hasAnyPosterResizeErroredOut = useHasAnyPosterResizeErroredForAccount(account.id, platformMediaTypeRef.current);
  const isAnyPosterLoading = useIsAnyPosterLoadingForAccount(account.id, platformMediaTypeRef.current);
  const doesAnyPosterNeedResize = useDoesAnyPosterRequireResizeForAccount(account.id, platformMediaTypeRef.current);
  const processingResizeAjaxParamsRef = useRef<SocialMediaResizeAjaxParams[]>([]);
  const storyGuideline = useStoryGuidelineForAccount(account.id);
  const publishableUrls = usePublishableURLsForAccount();
  const [isPlatformAnimationRunning, setIsPlatformAnimationRunning] = useState(false);
  const idPost = getPostId();

  const getDefaultResizeAjaxParamsForPoster = (posterId: PosterHashedId): SocialMediaResizeAjaxParams => {
    const socialMediaPlatform = account.type;

    const ajaxResizeParams: SocialMediaResizeAjaxParams = {
      posterId: posterId ?? '',
      resizeType: ResizeOptionsConfig.FILL,
      idPost: wizardPost?.id ?? '',
      socialMediaPlatform,
      socialAccountId: account.id,
      isVideo: designsGraphicsMetaForAccount[posterId].isVideo,
      platformMediaType: platformMediaTypeRef.current,
    };

    if (!publishingParams[account.id][platformMediaTypeRef.current]) {
      console.error('No publishing params found for account');
      return ajaxResizeParams;
    }

    return ajaxResizeParams;
  };

  const hasPosterBeenAlreadyDefaultResized = (posterId: string): boolean => {
    if (!publishableUrls[account.id]) {
      console.error('No publishable URL found for account with ID: ', account.id);
      return false;
    }

    const publishableUrlForAccount = publishableUrls[account.id];
    const mediaType = platformMediaTypeRef.current;

    const publishableUrlsForPlatformMediaType = publishableUrlForAccount[mediaType];

    if (!publishableUrlsForPlatformMediaType) {
      console.error('No publishable URL found for account with ID: ', account.id, ' and platform media type: ', mediaType);
      return false;
    }

    const publishableUrlForDesign = publishableUrlsForPlatformMediaType[posterId];

    if (!publishableUrlForDesign) {
      console.error('No publishable URL found for account with ID: ', account.id, ', platform media type: ', mediaType, ' and design ID: ', posterId);
      return false;
    }

    const originalGraphicUrl = designsGraphicsMeta[posterId].graphicURL;

    if (!accountNeedsResizingMapForDesigns) {
      return false;
    }

    const resizeRequiredForAccount = accountNeedsResizingMapForDesigns[posterId];
    let needsResize: PosterResizeRequiredStatus | undefined = resizeRequiredForAccount.needsResizing;

    if (platformMediaTypeRef.current === PlatformMediaType.STORIES) {
      needsResize = resizeRequiredForAccount.storyNeedsResizing;
    }

    if (platformMediaTypeRef.current === PlatformMediaType.REELS) {
      needsResize = resizeRequiredForAccount.reelNeedsResizing;
    }

    const isResizeNeeded =
      needsResize !== undefined && (needsResize === PosterResizeRequiredStatus.RESIZE_NEEDED || needsResize === PosterResizeRequiredStatus.BACKEND_OPTIMIZATION_NEEDED);

    if (!isResizeNeeded) {
      return true;
    }

    return isResizeNeeded && publishableUrlForDesign !== originalGraphicUrl;
  };

  const haveAllPosterBeenAlreadyDefaultResized = (): boolean => {
    if (!accountNeedsResizingMapForDesigns) {
      return true;
    }

    const posterIds = Object.keys(accountNeedsResizingMapForDesigns);

    for (let i = 0; i < posterIds.length; i++) {
      if (!hasPosterBeenAlreadyDefaultResized(posterIds[i])) {
        return false;
      }
    }

    return true;
  };

  const getDefaultAjaxParamsForAllPosters = useCallback((): null | SocialMediaResizeAjaxParams[] => {
    if (!accountNeedsResizingMapForDesigns || !account || haveAllPosterBeenAlreadyDefaultResized()) {
      return null;
    }

    const resizeAjaxParamsForPosters: SocialMediaResizeAjaxParams[] = [];
    const posterIds = Object.keys(accountNeedsResizingMapForDesigns);

    for (let i = 0; i < posterIds.length; i++) {
      const posterId = posterIds[i];
      const resizeInfoForPoster = accountNeedsResizingMapForDesigns[posterId];
      if (resizeInfoForPoster && !hasPosterBeenAlreadyDefaultResized(posterId)) {
        const resizeAjaxParams = getDefaultResizeAjaxParamsForPoster(posterId);
        resizeAjaxParamsForPosters.push(resizeAjaxParams);
      }
    }

    return resizeAjaxParamsForPosters;
  }, [accountNeedsResizingMapForDesigns]);

  const runIncomingAnimation = (): void => {
    if (previewParentRef && previewParentRef.current) {
      previewParentRef.current.classList.remove(styles.opacityFade);
      previewParentRef.current.classList.add(styles.fade);
    }
  };

  const runOutgoingAnimation = (): void => {
    setTimeout((): void => {
      if (previewParentRef && previewParentRef.current) {
        previewParentRef.current.classList.add(styles.fadeBack);
      }
    }, 500);
  };

  const onCaptionTransitionEnd = (event: TransitionEvent): void => {
    if (event.target === previewParentRef.current) {
      if (previewParentRef.current.classList.contains(styles.fadeBack)) {
        previewParentRef.current.classList.remove(styles.fade);
        previewParentRef.current.classList.remove(styles.fadeBack);
        previewParentRef.current.classList.add(styles.opacityFade);
      }
    }
  };

  const onSocialPostCaptionEvent = (): void => {
    if (previewParentRef && previewParentRef.current) {
      runIncomingAnimation();
      runOutgoingAnimation();
    }
  };

  const onPlatformSelectionTransitionEvent = (event: Event): void => {
    const customEvent = event as CustomEvent;
    const detail = customEvent.detail as SocialPostPlatformSelectionTransitionEventDetail;

    const platformMediaTypeFromEvent = detail.platformSelection;
    const {accountId} = detail;

    if (account.id !== accountId) {
      return;
    }

    if (platformMediaTypeFromEvent === platformMediaTypeRef.current && animatingElementRef && animatingElementRef.current) {
      animatingElementRef?.current?.classList.add(styles.fadeIn);
      setIsPlatformAnimationRunning(true);
    }
  };

  const onMediaTypeAnimationEnd = (): void => {
    if (!animatingElementRef?.current) {
      return;
    }

    animatingElementRef.current.classList.remove(styles.fadeIn);
    setIsPlatformAnimationRunning(false);
  };

  useEffect(() => {
    window.addEventListener(SocialPostCaptionOverwriteEvent, onSocialPostCaptionEvent);
    window.addEventListener(SocialPostPlatformSelectionTransitionEvent, onPlatformSelectionTransitionEvent);

    return (): void => {
      window.removeEventListener(SocialPostCaptionOverwriteEvent, onSocialPostCaptionEvent);
      window.removeEventListener(SocialPostPlatformSelectionTransitionEvent, onPlatformSelectionTransitionEvent);
    };
  }, [platformMediaType]);

  const trackGAEventForAutoResize = (resizeType: ResizeOptionsConfig | undefined): void => {
    if (resizeType) {
      trackPublishingGA4Events(GA4EventName.SOCIALWIZARD_AUTORESIZE, {
        [GA4EventParamName.TYPE]: getResizeConfigForGATracking(resizeType),
        [GA4EventParamName.PLATFORM]: getSocialPlatformName(account.type),
      });
    }
  };

  const updateResizingStatus = (accountResizeStatusToSet: AccountResizeStatus, posterId: string, platformMediaType: PlatformMediaType): void => {
    dispatch(
      setAccountResizeStatusForMediaType({
        idSocialMediaAccount: account.id,
        postResizeStatus: platformMediaType === PlatformMediaType.POST ? accountResizeStatusToSet : null,
        reelResizeStatus: platformMediaType === PlatformMediaType.REELS ? accountResizeStatusToSet : null,
        storyResizeStatus: platformMediaType === PlatformMediaType.STORIES ? accountResizeStatusToSet : null,
        idPoster: posterId,
      } as AccountResizeStatusForDesignPayload)
    );
  };

  const doStartDefaultResizingforPoster = (ajaxParamsForAccount: SocialMediaResizeAjaxParams): void => {
    const {posterId} = ajaxParamsForAccount;
    const {resizeType} = ajaxParamsForAccount;
    const originalPreviewUrl = designsGraphicsMetaForAccount[posterId].graphicURL;
    const {isVideo} = designsGraphicsMetaForAccount[posterId];

    updateResizingStatus(AccountResizeStatus.RESIZING, posterId, platformMediaTypeRef.current);
    updateGraphicUrlInGraphicsMetaForDesign('', posterId, isVideo);
    setIsPreviewLoading(true);
    trackGAEventForAutoResize(resizeType);

    makeSocialMediaResizeAjaxCall(ajaxParamsForAccount, isVideo)
      .then((s3FilePath: string) => {
        updateGraphicUrlInGraphicsMetaForDesign(s3FilePath, posterId, isVideo);

        dispatch(
          updatePublishableUrl({
            accountId: account.id,
            posterId,
            platformMediaType: platformMediaTypeRef.current,
            graphicUrl: s3FilePath,
          } as PublishableUrlUpdatePayload)
        );

        updateResizingStatus(AccountResizeStatus.RESIZE_DONE, posterId, platformMediaTypeRef.current);
      })
      .catch(() => {
        updateResizingStatus(AccountResizeStatus.RESIZE_ERROR, posterId, platformMediaTypeRef.current);
        updateGraphicUrlInGraphicsMetaForDesign(originalPreviewUrl, posterId, isVideo);
        throw new Error(`Unable to resize poster with the id: ${posterId}`);
      });
  };

  const handleDefaultResize = async (): Promise<void> => {
    const ajaxParamsForAllPosters = getDefaultAjaxParamsForAllPosters();

    if (!ajaxParamsForAllPosters) {
      return;
    }

    processingResizeAjaxParamsRef.current = ajaxParamsForAllPosters;

    for (let i = 0; i < ajaxParamsForAllPosters.length; i++) {
      try {
        doStartDefaultResizingforPoster(ajaxParamsForAllPosters[i]);
      } catch (e) {
        console.log(e);
      }
    }
  };

  const triggerDefaultResizing = async (): Promise<void> => {
    if (processingResizeAjaxParamsRef.current.length !== 0) {
      return;
    }

    void handleDefaultResize().then((): void => {
      processingResizeAjaxParamsRef.current = [];
    });
  };

  useEffect((): void => {
    void triggerDefaultResizing();
  }, []);

  const setTitleError = (newValue: string | null): void => {
    setTitleErrorMessage(newValue);
  };

  const getImagePublishFormElements = (): ReactElement<any> | null => {
    switch (account.type) {
      case SocialPlatform.FACEBOOK:
      case SocialPlatform.FACEBOOK_PROFILE:
      case SocialPlatform.FACEBOOK_PAGE:
      case SocialPlatform.FACEBOOK_GROUP:
      case SocialPlatform.INSTAGRAM:
      case SocialPlatform.TWITTER:
      case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
      case SocialPlatform.THREADS:
        return <SocialPostCaption account={account} />;
      case SocialPlatform.LINKEDIN:
      case SocialPlatform.LINKEDIN_PROFILE:
        return (
          <>
            <SocialPostCaption account={account} />
            <FieldSpacer />
            <LinkedInPrivacySelector account={account} />
          </>
        );
      case SocialPlatform.LINKEDIN_PAGE:
        return <SocialPostCaption account={account} />;
      case SocialPlatform.TIKTOK:
        return (
          <>
            <TitleInput account={account} />
            <FieldSpacer />
            <SocialPostCaption account={account} />
            <FieldSpacer />
            <TiktokPublishingOptions account={account} />
          </>
        );
      case SocialPlatform.YOUTUBE:
        return (
          <>
            <TitleInput setTitleError={setTitleError} account={account} areURLsAllowed={false} />
            <FieldSpacer />
            <SocialPostCaption account={account} />
            <FieldSpacer />
            <YouTubePrivacySelector account={account} />
          </>
        );
      case SocialPlatform.PINTEREST:
        return (
          <>
            <PinterestBoardId account={account} />
            <FieldSpacer />
            <TitleInput setTitleError={setTitleError} account={account} />
            <FieldSpacer />
            <SocialPostCaption account={account} />
            <FieldSpacer />
            <PinterestWebsiteLink account={account} />
            <FieldSpacer />
          </>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (isAnyPosterResizingForAccount) {
      setIsPreviewLoading(true);
    }

    if (hasAnyPosterResizeErroredOut) {
      setIsPreviewLoading(false);
    }
  }, [isAnyPosterResizingForAccount, hasAnyPosterResizeErroredOut]);

  const onReelToggle = (newReelToggleValue: boolean): void => {
    if (newReelToggleValue && storyPublishingParamsForAccount) {
      dispatch(setMediaTypeSelectionToReelAndStory(account.id));
      return;
    }

    if (newReelToggleValue) {
      dispatch(setMediaTypeSelectionToReel(account.id));
      return;
    }

    if (storyPublishingParamsForAccount) {
      dispatch(setMediaTypeSelectionToPostAndStory(account.id));
      return;
    }

    dispatch(setMediaTypeSelectionToPost(account.id));
  };

  const getVideoPublishFormElements = (): ReactElement<any> | null => {
    switch (account.type) {
      case SocialPlatform.FACEBOOK:
      case SocialPlatform.FACEBOOK_PROFILE:
      case SocialPlatform.FACEBOOK_PAGE:
        return (
          <>
            <ReelToggler
              disabledTooltipText={isAnyPosterResizingForForAnyMediaType ? window.i18next.t('pmwjs_your_design_is_being_resized') : undefined}
              isDisabled={isAnyPosterResizingForForAnyMediaType}
              isToggled={platformMediaTypeRef.current === PlatformMediaType.REELS}
              onReelToggle={onReelToggle}
            />
            <FieldSpacer />
            <TitleInput account={account} />
            <FieldSpacer />
            <SocialPostCaption account={account} />
          </>
        );
      case SocialPlatform.FACEBOOK_GROUP:
        return (
          <>
            <TitleInput account={account} />
            <FieldSpacer />
            <SocialPostCaption account={account} />
          </>
        );
      case SocialPlatform.INSTAGRAM:
        return <SocialPostCaption account={account} />;
      case SocialPlatform.TIKTOK:
        return (
          <>
            <SocialPostCaption account={account} />
            <FieldSpacer />
            <TiktokPublishingOptions account={account} />
          </>
        );
      case SocialPlatform.TWITTER:
        return <SocialPostCaption account={account} />;
      case SocialPlatform.LINKEDIN:
      case SocialPlatform.LINKEDIN_PROFILE:
        return (
          <>
            <TitleInput setTitleError={setTitleError} account={account} />
            <FieldSpacer />
            <SocialPostCaption account={account} />
            <FieldSpacer />
            <LinkedInPrivacySelector account={account} />
            <FieldSpacer />
          </>
        );
      case SocialPlatform.LINKEDIN_PAGE:
        return (
          <>
            <TitleInput setTitleError={setTitleError} account={account} />
            <FieldSpacer />
            <SocialPostCaption account={account} />
          </>
        );
      case SocialPlatform.YOUTUBE:
        return (
          <>
            <TitleInput setTitleError={setTitleError} account={account} areURLsAllowed={false} />
            <FieldSpacer />
            <SocialPostCaption account={account} />
            <FieldSpacer />
            <YouTubePrivacySelector account={account} />
            {getTitleError(titleErrorMessage)}
          </>
        );
      case SocialPlatform.PINTEREST:
        return (
          <>
            <PinterestBoardId account={account} />
            <FieldSpacer />
            <TitleInput setTitleError={setTitleError} account={account} />
            <FieldSpacer />
            <SocialPostCaption account={account} />
            <FieldSpacer />
            <PinterestWebsiteLink account={account} />
          </>
        );
      case SocialPlatform.THREADS:
        return <SocialPostCaption account={account} />;
      default:
        return null;
    }
  };

  const getTitleError = (errorMessage: string | null): ReactElement<any> | null => {
    if (!errorMessage) {
      return null;
    }
    return (
      <div className="flex-row-align-center spacing-m-t-2">
        <Icon icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_16} className="spacing-m-r-2 content-danger" />
        <Text className="content-danger" size={TextSize.XSMALL} val={errorMessage} />
      </div>
    );
  };

  const getFormElementsIfNeeded = (): ReactElement<any> | null => {
    if (platformMediaType === PlatformMediaType.STORIES && publishingAction === undefined) {
      return null;
    }

    if (publishingAction === undefined) {
      return getFormElementsByGraphicType();
    }

    return <PublishingPost platformMediaType={platformMediaTypeRef.current} publishingAction={publishingAction} account={account} />;
  };

  const isPublishingActionHidingMultiplePosts = (): boolean => {
    return publishingAction === SocialPostPublishingAction.SCHEDULE || publishingAction === SocialPostPublishingAction.SAVE;
  };

  const getClassForPublishingAction = (): string => {
    if (isPublishingActionHidingMultiplePosts() && publishingParams && accountIdsToPublishTo.length > 0) {
      const firstAccountId: number = accountIdsToPublishTo[0];

      if (account.id !== firstAccountId) {
        return '_invisible';
      }
    }

    return '';
  };

  const getFormElementsByGraphicType = (): ReactElement<any> | null => {
    if (isVideoDesign) {
      return getVideoPublishFormElements();
    }

    return getImagePublishFormElements();
  };

  const updateGraphicUrlInGraphicsMetaForDesign = useCallback((graphicURL: string, posterId: string, isVideo: boolean): void => {
    dispatch(
      updateGraphicURLInGraphicsMetaForDesign({
        idSocialMediaAccount: account.id,
        designId: posterId,
        previewURL: graphicURL,
        isVideo,
        mediaType: platformMediaType,
      })
    );
  }, []);

  const getFieldSpaceIfRequired = (): ReactElement<any> | null => {
    if (isPublishingActionHidingMultiplePosts()) {
      return null;
    }

    return <FieldSpacer />;
  };

  const getPreviewOverlay = (): ReactElement<any> => {
    if (loadingState !== LoadingStates.LOADING || wizardStep !== SocialMediaWizardStep.PUBLISH) {
      return <></>;
    }

    return <div className={styles.loadingOverlay} />;
  };

  const onLoadCallback = (): void => {
    setIsPreviewLoading(false);
  };

  const getSelectionIndexForRadioButton = (): string => {
    const isReelOrPost = postPublishingParamsForAccount !== null || reelPublishingParamsForAccount !== null;

    if (storyPublishingParamsForAccount && isReelOrPost) {
      return SocialMediaWizardMediaTypeRadioButtonIds.BOTH;
    }

    if (storyPublishingParamsForAccount) {
      return SocialMediaWizardMediaTypeRadioButtonIds.STORY;
    }

    return SocialMediaWizardMediaTypeRadioButtonIds.POST;
  };

  const onSelectionChange = (selectedId: SocialMediaWizardMediaTypeRadioButtonIds): void => {
    if (selectedId === SocialMediaWizardMediaTypeRadioButtonIds.POST) {
      dispatch(setMediaTypeSelectionToPost(account.id));
    }

    if (selectedId === SocialMediaWizardMediaTypeRadioButtonIds.STORY) {
      dispatch(setMediaTypeSelectionToStory(account.id));
    }

    if (selectedId === SocialMediaWizardMediaTypeRadioButtonIds.BOTH) {
      if (reelPublishingParamsForAccount) {
        dispatch(setMediaTypeSelectionToReelAndStory(account.id));
        return;
      }

      if (postPublishingParamsForAccount || storyPublishingParamsForAccount) {
        dispatch(setMediaTypeSelectionToPostAndStory(account.id));
      }
    }
  };

  const shouldShowPlatformMediaTypeRadioButtons = (): boolean => {
    if (publishingAction !== undefined) {
      return false;
    }

    const isMultiPosting = designIdsToPublishTo.length > 1;

    if (isMultiPosting || (!isFacebookAccount(account) && !isInstagramAccount(account))) {
      return false;
    }

    if (isFacebookAccount(account) && isVideoDesign) {
      return false;
    }

    return true;
  };

  const getPlatformMediaTypeRadioButtons = (): ReactElement<any> | null => {
    const radioButtonOptions: RadioButtonOption[] = [
      {
        text: window.i18next.t('pmwjs_post'),
        id: SocialMediaWizardMediaTypeRadioButtonIds.POST,
      },
      {
        text: window.i18next.t('pmwjs_story'),
        id: SocialMediaWizardMediaTypeRadioButtonIds.STORY,
      },
      {
        text: window.i18next.t('pmwjs_both'),
        id: SocialMediaWizardMediaTypeRadioButtonIds.BOTH,
      },
    ];

    if (!shouldShowPlatformMediaTypeRadioButtons()) {
      return null;
    }

    return (
      <RadioButton
        isDisabled={isAnyPosterResizingForForAnyMediaType}
        disabledTooltipText={isAnyPosterResizingForForAnyMediaType ? window.i18next.t('pmwjs_your_design_is_being_resized') : undefined}
        containerClasses={getRadioButtonContainerClasses()}
        selectionId={getSelectionIndexForRadioButton()}
        options={radioButtonOptions}
        onSelectionCallback={(selectedId: string) => {
          onSelectionChange(selectedId as SocialMediaWizardMediaTypeRadioButtonIds);
        }}
      />
    );
  };

  const getRadioButtonContainerClasses = (): string => {
    return `${styles.transitionClasses} ${isPlatformAnimationRunning ? styles.fadeIn : ''}`;
  };

  const getStoryGuidelineIfNeeded = (): ReactElement<any> | null => {
    if (publishingAction !== undefined) {
      return null;
    }

    if (platformMediaTypeRef.current === PlatformMediaType.STORIES) {
      return <SocialMediaPublishParamsStoryGuideline storyGuideline={storyGuideline} />;
    }

    return null;
  };

  const getCoverUrlForSingleGraphicPost = useMemo(() => {
    const graphicsMeta = designsGraphicsMetaForAccount[designIdsToPublishTo[0]];

    if (!graphicsMeta?.cover) {
      return 'no-cover-url';
    }

    return graphicsMeta.cover.coverUrl;
  }, [designsGraphicsMetaForAccount[designIdsToPublishTo[0]]?.cover?.coverUrl]);

  const onRemoveAccountClick = async (): Promise<void> => {
    const hideRemoveConfirmationPrompt = await getHideSMRemoveAccountConfirmationPreference();

    if (!hideRemoveConfirmationPrompt) {
      openRemoveAccountConfirmationPrompt();
      return;
    }

    doRemoveAccountFromPost();
  };

  const doRemoveAccountFromPost = (): void => {
    const postHasSingleAccount = accountIdsToPublishTo.length === 1;

    if (postHasSingleAccount) {
      dispatch(updatePublishingParams({}));
      dispatch(updateWizardStep(SocialMediaWizardStep.SELECT_ACCOUNTS));
      return;
    }

    dispatch(removeAccountIdFromPublishing(account.id));
    dispatch(removeAccountFromPublishingParams(account.id));
  };

  const onRemoveAccountPromptPermanentClosureConfirmation = (): void => {
    void doHideRemoveAccountConfirmationForUser();
  };

  const openRemoveAccountConfirmationPrompt = (): void => {
    const isSmallScreen = !isWeb;

    openConfirmDeleteModal({
      title: window.i18next.t('pmwjs_remove_account'),
      text: window.i18next.t('pmwjs_removing_account_description'),
      warningIcon: 'icon-delete',
      ctaText: window.i18next.t('pmwjs_remove'),
      onDeleteConfirmation: doRemoveAccountFromPost,
      ctaClasses: isSmallScreen ? '_full-width' : '',
      footerActionContainerClasses: isSmallScreen ? 'flex-v-row' : '',
      showPermanentClosureCheckbox: true,
      onPermanentClosureCheckboxConfirmation: onRemoveAccountPromptPermanentClosureConfirmation,
    });
  };

  const getWebContent = (): ReactElement<any> => {
    return (
      <div key={account.id} className="flex-h-row _full-width">
        <div className={`flex-v-row flex-content-start spacing-m-r-10 ${styles.contentContainer} ${getClassForPublishingAction()}`}>
          <SocialAccountLabel
            platformMediaType={platformMediaTypeRef.current}
            account={account}
            showAccountDetails
            className={isPublishingActionHidingMultiplePosts() ? '_hidden' : ''}
            onRemoveAccountClick={(): void => {
              void onRemoveAccountClick();
            }}
          />
          {getFieldSpaceIfRequired()}
          {getPlatformMediaTypeRadioButtons()}
          {getFieldSpaceIfRequired()}
          {getFormElementsIfNeeded()}
          {getStoryGuidelineIfNeeded()}
        </div>
        <div ref={animatingElementRef} onAnimationEnd={onMediaTypeAnimationEnd} className={styles.previewContent}>
          {getPreviewOverlay()}
          <div
            onTransitionEnd={onCaptionTransitionEnd}
            ref={previewParentRef}
            className={`${styles.previewWrapper} ${!isPlatformAnimationRunning ? styles.previewWrapperColor : ''} ${isPreviewLoading ? '' : styles.loaded}`}
            key={getCoverUrlForSingleGraphicPost}
          >
            <SocialPostContent
              designIdsToPublish={designIdsToPublishTo}
              onLoadCallback={onLoadCallback}
              areDetailsLoading={false}
              parentContainerRef={previewParentRef}
              type={hasAnyPosterResizeErroredOut ? SocialPostContentType.MEDIA_RESIZING_FAILURE : SocialPostContentType.ACCOUNT_PREVIEW}
              account={account}
              accountsToPublishTo={[]}
              publishingParams={publishingParams}
              designsGraphicsMeta={designsGraphicsMetaForAccount}
              platformMediaType={platformMediaTypeRef.current}
            />
            <div className={`spacing-m-b-3 spacing-m-t-15 ${styles.ctaWrapper}`}>{getPreviewCta()}</div>
          </div>
        </div>
      </div>
    );
  };

  const handleResizeCompleted = (graphicURL: string, posterId: PosterHashedId): void => {
    if (!posterId) {
      console.error('No poster was selected for user triggered resizing');
      return;
    }

    const {isVideo} = designsGraphicsMetaForAccount[posterId];

    dispatch(
      updatePublishableUrl({
        accountId: account.id,
        posterId,
        platformMediaType: platformMediaTypeRef.current,
        graphicUrl: graphicURL,
      } as PublishableUrlUpdatePayload)
    );

    updateGraphicUrlInGraphicsMetaForDesign(graphicURL, posterId, isVideo);
    updateResizingStatus(AccountResizeStatus.RESIZE_DONE, posterId, platformMediaTypeRef.current);
    setIsPreviewLoading(false);
  };

  const handleResizeError = (posterIdThatHasErrored: PosterHashedId): void => {
    const originalPreviewUrl = designsGraphicsMetaForAccount[posterIdThatHasErrored].graphicURL;
    const {isVideo} = designsGraphicsMetaForAccount[posterIdThatHasErrored];

    updateResizingStatus(AccountResizeStatus.RESIZE_ERROR, posterIdThatHasErrored, platformMediaTypeRef.current);
    updateGraphicUrlInGraphicsMetaForDesign(originalPreviewUrl, posterIdThatHasErrored, isVideo);
  };

  const handleCancelResize = (): void => {};

  const handleDoneClicked = (posterId: PosterHashedId): void => {
    if (!posterId) {
      console.error('No poster was selected for resize');
      return;
    }

    setIsPreviewLoading(true);
    const {isVideo} = designsGraphicsMetaForAccount[posterId];
    updateGraphicUrlInGraphicsMetaForDesign('', posterId, isVideo);
    updateResizingStatus(AccountResizeStatus.RESIZING, posterId, platformMediaTypeRef.current);
  };

  const handleResizeOrRetryClick = (): void => {
    if (isRetryCta()) {
      void triggerDefaultResizing();
      return;
    }

    onClickResizeDesign();
  };

  const isRetryCta = (): boolean => {
    return accountResizeStatus === AccountResizeStatus.RESIZE_ERROR;
  };

  const isResizeCta = (): boolean => {
    return publishingAction === undefined && doesAnyPosterNeedResize;
  };

  const shouldHideResizeButton = (): boolean => {
    switch (account.type) {
      case SocialPlatform.PINTEREST:
        return true;
      default:
        return false;
    }
  };

  const getResizeCta = (): ReactElement<any> => {
    return (
      <Button
        key="resize-cta"
        ref={resizeButtonRef}
        icon="icon-resize"
        id="add-details-onboarding-resize-button"
        type={Type.SECONDARY}
        size={Size.SMALL}
        text={window.i18next.t('pmwjs_resize_options')}
        onClick={onClickResizeDesign}
        customClasses={`spacing-m-r-2 ${styles.previewShadow} ${styles.hiddenEditHolder} ${!isAnyPosterLoading ? styles.editDesignHolder : ''}`}
      />
    );
  };

  const shouldShowCommonCoverCheckBoxForCoverPanel = (): boolean => {
    const accountsSupportingPostCoverToPublishTo = accountsToPublishTo.filter((account) => socialAccountPostCoverValidations[account.type]?.doesSupportCover);

    return accountsSupportingPostCoverToPublishTo.length !== 1;
  };

  const getTextForCoverCta = (): string => {
    if (designsGraphicsMetaForAccount[designIdsToPublishTo[0]].cover) {
      return window.i18next.t('pmwjs_edit_cover');
    }

    return window.i18next.t('pmwjs_add_cover');
  };

  const isCoverCta = (): boolean => {
    if (!isVideoDesign || platformMediaTypeRef.current === PlatformMediaType.STORIES) {
      return false;
    }
    return designIdsToPublishTo.length === 1 && socialAccountPostCoverValidations[account.type].doesSupportCover;
  };

  const getNewFeatureIndicatorForCoverCta = (): ReactElement<any> | null => {
    if (isWeb) {
      return (
        <Pill
          className={`_position-absolute spacing-p-2 ${styles.pillCoverCTA} ${isPreviewLoading ? '_hidden' : ''}`}
          background={PILL_BACKGROUND_COLOR.SECONDARY_100}
          content={{
            text: window.i18next.t('pmwjs_new'),
            type: PILL_CONTENT_TYPE.TEXT,
            textColor: 'content-body-white',
            textSize: TextSize.XXSMALL,
          }}
          size={PILL_SIZE.XSMALL}
        />
      );
    }

    return <PulsatingDot containerClassname={`_position-absolute ${styles.pulsatingDotCoverCta} ${isPreviewLoading ? '_hidden' : ''}`} />;
  };

  const getCoverCta = (): ReactElement<any> => {
    return (
      <div className="_position-relative">
        {getNewFeatureIndicatorForCoverCta()}
        <Button
          key="cover-cta"
          icon="icon-image"
          type={Type.SECONDARY}
          size={Size.SMALL}
          text={getTextForCoverCta()}
          onClick={onClickCover}
          customClasses={`spacing-m-r-2 ${styles.previewShadow} ${styles.hiddenEditHolder} ${!isAnyPosterLoading && !isPreviewLoading ? styles.editDesignHolder : '_hidden'}`}
        />
      </div>
    );
  };

  const getRetryCta = (): ReactElement<any> => {
    return (
      <Button
        key="retry-cta"
        ref={resizeButtonRef}
        icon="icon-refresh"
        type={Type.SECONDARY}
        size={Size.SMALL}
        text={window.i18next.t('pmwjs_retry')}
        onClick={handleResizeOrRetryClick}
        customClasses={`spacing-m-r-2 ${styles.previewShadow} ${styles.hiddenEditHolder} ${!isAnyPosterLoading ? styles.editDesignHolder : ''}`}
      />
    );
  };

  const getPreviewCta = (): ReactElement<any>[] | null => {
    if (wizardStep === SocialMediaWizardStep.PUBLISH) {
      return null;
    }

    if (isRetryCta()) {
      return [getRetryCta()];
    }

    const ctas: ReactElement<any>[] = [];

    if (isResizeCta() && !shouldHideResizeButton()) {
      ctas.push(getResizeCta());
    }

    if (isCoverCta()) {
      ctas.push(getCoverCta());
    }

    return ctas.length > 0 ? ctas : null;
  };

  const onResizeDesignSelectionCallback = (posterHashedId: string): void => {
    dispatch(updateDesignIdToResize(posterHashedId));
    dispatch(updateMultiGraphicDesignSelectorMode(MultiGraphicDesignSelectorModes.RESIZE));
  };

  const getTimestampForPostCover = (): number => {
    const coverObject = designsGraphicsMetaForAccount[designIdsToPublishTo[0]].cover;

    if (!coverObject || coverObject.timestamp === null) {
      return 0;
    }

    return coverObject.timestamp;
  };

  const isCoverUrlforUserUploadCover = (): boolean => {
    const coverObject = designsGraphicsMetaForAccount[designIdsToPublishTo[0]].cover;

    return !!coverObject && coverObject.timestamp === null;
  };

  const getUrlForUserUploadCover = (): string | undefined => {
    if (!isCoverUrlforUserUploadCover()) {
      return undefined;
    }

    const coverObject = designsGraphicsMetaForAccount[designIdsToPublishTo[0]].cover;
    if (!coverObject?.coverUrl) {
      return undefined;
    }

    return coverObject.coverUrl;
  };

  const handleCoverGenerationRequest = async (socialPostCoverParams: SocialPostCoverParams, modalId: string): Promise<void> => {
    try {
      const cover = await generateCoverForPost(socialPostCoverParams);
      dispatch(closeModal(modalId));

      dispatch(
        updatePublishingParamSocialPostCover({
          idDesign: designIdsToPublishTo[0],
          idAccount: account.id,
          coverObject: cover,
          platformMediaType,
        })
      );
    } catch (error) {
      console.error('Error during cover generation:', error);
      handleCoverGenerationError(socialPostCoverParams.source);
    }
  };

  const handleCoverGenerationError = (source: CoverSource): void => {
    switch (source) {
      case CoverSource.DESIGN:
        dispatch(setIsLoading(false));
        dispatch(setShowCoverGenerationError(true));
        break;

      case CoverSource.UPLOAD:
        dispatch(setShowLoading(false));
        dispatch(
          setPanelMessage({
            type: PANEL_MESSAGE_TYPE.ERROR,
            message: window.i18next.t('pmwjs_unable_to_generate_cover'),
          })
        );
        break;

      default:
        console.error('Unknown cover source');
    }
  };

  const onDoneClickForUserUploadedCoverSelected = (): void => {
    dispatch(closeModal(MODALS_IDS.SOCIAL_MEDIA_SELECT_COVER_MODAL));
    const currentUploadCoverUrl = getUrlForUserUploadCover();

    if (!currentUploadCoverUrl) {
      return;
    }

    const cover: SocialPostCover = {
      timestamp: null,
      coverUrl: getCacheBustedURL(currentUploadCoverUrl),
    };

    dispatch(
      updatePublishingParamSocialPostCover({
        idDesign: designIdsToPublishTo[0],
        idAccount: account.id,
        coverObject: cover,
        platformMediaType,
      })
    );
  };

  const onSelectCoverPanelDoneClick = (currentCoverTimestamp: number, isUserUploadedCoverSelected: boolean): void => {
    if (isUserUploadedCoverSelected) {
      onDoneClickForUserUploadedCoverSelected();
      return;
    }

    onDoneClickForTimestampCoverSelected(currentCoverTimestamp);
  };

  const onDoneClickForTimestampCoverSelected = (coverTimestamp: number): void => {
    if (!idPost) {
      window.PMW.openErrorModal({message: window.i18next.t('pmwjs_error_message')});
      return;
    }

    const socialPostCoverParams: SocialPostCoverParams = {
      source: CoverSource.DESIGN,
      timestampedCoverParams: {
        graphicUrl: designsGraphicsMetaForAccount[designIdsToPublishTo[0]].graphicURL,
        timestamp: coverTimestamp,
        idDesign: designIdsToPublishTo[0],
        idPost,
        idAccount: account.id,
      },
    };

    dispatch(setIsLoading(true));
    void handleCoverGenerationRequest(socialPostCoverParams, MODALS_IDS.SOCIAL_MEDIA_SELECT_COVER_MODAL);
  };

  const onUploadImageCrop = (hashedFileName: string, fileExtension: string, cropDataResponse: CropperPanelApplyResponse): void => {
    if (!idPost) {
      window.PMW.openErrorModal({message: window.i18next.t('pmwjs_error_message')});
      return;
    }

    const cropDataForParams: CropData = {
      x: cropDataResponse.cropData.x,
      y: cropDataResponse.cropData.y,
      width: cropDataResponse.cropData.width,
      height: cropDataResponse.cropData.height,
      imageWidth: cropDataResponse.cropData.imageWidth,
      imageHeight: cropDataResponse.cropData.imageHeight,
    };

    const socialPostCoverParams: SocialPostCoverParams = {
      source: CoverSource.UPLOAD,
      uploadedCoverPostParams: {
        uploadImageHashedFileName: hashedFileName,
        uploadImageFileExtension: fileExtension,
        cropData: cropDataForParams,
      },
    };

    dispatch(setShowLoading(true));
    void handleCoverGenerationRequest(socialPostCoverParams, MODALS_IDS.CROPPER_MODAL);
  };

  const onClickCover = (): void => {
    if (designIdsToPublishTo.length !== 1) {
      return;
    }

    const timestamp = getTimestampForPostCover();
    const uploadCoverUrl = getUrlForUserUploadCover();

    const selectCoverModalProps: SocialMediaSelectCoverModalProps = {
      videoUrl: designsGraphicsMetaForAccount[designIdsToPublishTo[0]].graphicURL,
      previewAspectRatio: socialAccountPostCoverValidations[account.type].previewAspectRatio,
      onDone: onSelectCoverPanelDoneClick,
      onUploadImageCrop,
      coverTimestamp: timestamp,
      showUploadButton: socialAccountPostCoverValidations[account.type].isUploadAllowed,
      showCommonCoverCheckBox: shouldShowCommonCoverCheckBoxForCoverPanel(),
      account,
      uploadCoverUrl,
    };

    dispatch(setShowCoverGenerationError(false));
    openSelectCoverModal(selectCoverModalProps);
  };

  const setDesignActionPanelModeToSelect = (): void => {
    dispatch(updateMultiGraphicDesignSelectorMode(MultiGraphicDesignSelectorModes.SELECT));
  };

  const onClickResizeDesign = (): void => {
    if (designIdsToPublishTo.length === 1) {
      onResizeDesignSelectionCallback(designIdsToPublishTo[0]);
    } else {
      setDesignActionPanelModeToSelect();
    }

    const designActionModalProps: SocialPostsDesignActionModalProps = {
      account,
      designIdsToPublish: designIdsToPublishTo,
      designsGraphicsMeta,
      designActionCallback: onResizeDesignSelectionCallback,
      actionPurpose: MultiPostDesignActionPurposes.RESIZE,
      resizePanelOptions: {
        account,
        panelId: SocialPostSelectDesignPanelID,
        onCancelResize: handleCancelResize,
        onResizeCompleted: handleResizeCompleted,
        onResizeError: handleResizeError,
        onDoneClicked: handleDoneClicked,
        platformMediaType: platformMediaTypeRef.current,
      },
    };

    openDesignActionModal(designActionModalProps);
  };

  const getClassesForPreviewWrapper = (): string => {
    let previewWrapperClass = styles.previewWrapper;

    if (!isPreviewLoading) {
      previewWrapperClass += ` ${styles.loaded}`;
    }

    return previewWrapperClass;
  };

  const getMobilePreviewContent = (): ReactElement<any> => {
    return (
      <div key={`content-post-${account.id}`} className={`_full-width flex-column-justify-center flex-items-center ${styles.mobilePreviewWrapper}`}>
        <div onTransitionEnd={onCaptionTransitionEnd} ref={previewParentRef} className={getClassesForPreviewWrapper()} key={getCoverUrlForSingleGraphicPost}>
          <SocialPostContent
            designIdsToPublish={designIdsToPublishTo}
            onLoadCallback={onLoadCallback}
            areDetailsLoading={false}
            parentContainerRef={previewParentRef}
            type={SocialPostContentType.ACCOUNT_PREVIEW}
            account={account}
            accountsToPublishTo={[]}
            publishingParams={publishingParams}
            designsGraphicsMeta={designsGraphicsMetaForAccount}
            platformMediaType={platformMediaTypeRef.current}
          />
        </div>
        <div className={`spacing-m-b-3 spacing-m-t-15 ${styles.ctaWrapper}`}>{getPreviewCta()}</div>
      </div>
    );
  };

  const getMobilePreview = (): ReactElement<any> => {
    return getMobilePreviewContent();
  };

  const handleRemoveAccountClick = (): void => {
    void onRemoveAccountClick();
  };

  const getMobileContent = (): ReactElement<any> => {
    if (wizardMobileNavView === WizardMobileNavView.CONTENT) {
      return (
        <div ref={contentRef} className="flex-h-row _full-width">
          <div className={`flex-v-row _full-width ${isSmallScreen || isExtraSmallScreen ? '' : 'spacing-m-r-10'}`}>
            <SocialAccountLabel account={account} showAccountDetails onRemoveAccountClick={handleRemoveAccountClick} />
            <FieldSpacer />
            {getPlatformMediaTypeRadioButtons()}
            {shouldShowPlatformMediaTypeRadioButtons() ? <FieldSpacer /> : null}
            {getFormElementsIfNeeded()}
            {getStoryGuidelineIfNeeded()}
          </div>
        </div>
      );
    }

    return getMobilePreview();
  };

  return isWeb ? getWebContent() : getMobileContent();
}
